import { useState, useCallback, useEffect } from "react";
import { GitBranch } from "lucide-react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import type { DevAgentDependencySnapshot } from "@/stores/devAgentStore.types";
import { PanelSection } from "./PanelSection";
import { useAuthStoreV1 } from "@/stores";
import {
  isEditorSafeMetadataItemId,
  isSameDependencySnapshot,
  resolveDependencyItemForEditor,
} from "./resolveDependencyItemForEditor";

export function DependenciesPanel({
  disabled,
  onMoveUp,
  onMoveDown,
  onSendMessage,
  onOpenFileInEditor,
}: {
  disabled?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSendMessage: (msg: string) => void;
  onOpenFileInEditor: (file: {
    id: string;
    name: string;
    metadataType?: string;
  }) => void;
}) {
  const { deps, clearDeps } = useDevAgentStore(
    useShallow((s) => ({ deps: s.dependencies, clearDeps: s.clearDependencies }))
  );
  const snapshots = (deps || []).slice().sort((a, b) =>
    (b.captured_at || "").localeCompare(a.captured_at || ""),
  );
  const totalSnapshots = snapshots.length;

  const openByItem = useCallback(
    async (item: { name: string; id: string; metadata_type?: string }) => {
      const orgId = useAuthStoreV1.getState().selectedOrg?.id;
      const resolved = await resolveDependencyItemForEditor(item, orgId);
      if (!resolved) return;
      onOpenFileInEditor(resolved);
    },
    [onOpenFileInEditor],
  );

  const sendApproval = useCallback(
    async (
      snapshot: DevAgentDependencySnapshot,
      scope: "all" | "callers" | "tests" | "skip",
    ) => {
      const sessionUuid = useDevAgentStore.getState().sessionUuid;
      if (!sessionUuid) return;

      // Freeze deps at click time so a session switch cannot PATCH another
      // session's state with the wrong dependency list.
      const depsAtClick = [
        ...(useDevAgentStore.getState().dependencies || []),
      ];

      const word =
        scope === "all"
          ? `yes — update all callers and tests for ${snapshot.target}`
          : scope === "callers"
            ? `only callers for ${snapshot.target}`
            : scope === "tests"
              ? `only tests for ${snapshot.target}`
              : `skip deps for ${snapshot.target}`;
      onSendMessage(word);

      const newStatus = scope === "skip" ? "rejected" : "approved";
      const updated = depsAtClick.map((d) =>
        isSameDependencySnapshot(d, snapshot)
          ? { ...d, status: newStatus as DevAgentDependencySnapshot["status"] }
          : d,
      );

      try {
        const { updateSessionState } = await import("../../devAgentService");
        await updateSessionState(sessionUuid, { dependencies: updated as any });
        if (useDevAgentStore.getState().sessionUuid === sessionUuid) {
          useDevAgentStore.getState().setDependencies(updated);
        }
      } catch {
        /* non-fatal */
      }

      const toOpen: typeof snapshot.callers = [];
      if (scope === "all")
        toOpen.push(...snapshot.callers, ...snapshot.tests);
      else if (scope === "callers") toOpen.push(...snapshot.callers);
      else if (scope === "tests") toOpen.push(...snapshot.tests);
      for (const item of toOpen.slice(0, 5)) {
        void openByItem(item).catch(() => {});
      }
    },
    [onSendMessage, openByItem],
  );

  const [expandedTarget, setExpandedTarget] = useState<string | null>(null);
  useEffect(() => {
    if (snapshots.length > 0 && expandedTarget === null) {
      setExpandedTarget(snapshots[0].target);
    }
  }, [snapshots.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PanelSection
      title="Dependencies"
      icon={<GitBranch className="w-3.5 h-3.5" />}
      count={totalSnapshots}
      onClear={totalSnapshots > 0 ? clearDeps : undefined}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      disabled={disabled}
    >
      {totalSnapshots > 0 ? (
        <div className="px-2 py-1.5 space-y-1.5">
          {snapshots.map((snap) => {
            const isExpanded = expandedTarget === snap.target;
            /** Pending / awaiting states used amber-400 — too faint on light backgrounds. */
            const statusBadgeClass =
              snap.status === "approved"
                ? "font-semibold text-emerald-800 dark:text-emerald-400"
                : snap.status === "rejected"
                  ? "font-medium text-muted-foreground"
                  : snap.status === "applied"
                    ? "font-semibold text-blue-800 dark:text-blue-400"
                    : "font-semibold text-amber-950 bg-amber-400/35 border border-amber-800/30 dark:text-amber-100 dark:bg-amber-500/25 dark:border-amber-300/40 px-1 py-0.5 rounded";
            return (
              <div
                key={snap.target}
                className="rounded border border-border bg-muted/15"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpandedTarget(isExpanded ? null : snap.target)
                  }
                  className="w-full text-left px-2 py-1 flex items-center justify-between hover:bg-muted/20 rounded-t transition-colors"
                >
                  <span className="text-xs font-medium text-foreground truncate">
                    {snap.target}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {snap.callers.length}c · {snap.tests.length}t
                    </span>
                    <span
                      className={`text-[9px] uppercase tracking-wide shrink-0 max-w-[7.5rem] truncate ${statusBadgeClass}`}
                      title={snap.status?.replace(/_/g, " ")}
                    >
                      {snap.status?.replace(/_/g, " ").slice(0, 12)}
                    </span>
                  </span>
                </button>

                {isExpanded && (
                  <div className="px-2 py-1.5 space-y-1.5 border-t border-border">
                    {(snap.target_id || snap.target) && (
                      <button
                        type="button"
                        onClick={() =>
                          void openByItem({
                            name: snap.target,
                            id:
                              snap.target_id &&
                              isEditorSafeMetadataItemId(snap.target_id)
                                ? snap.target_id
                                : "",
                            metadata_type: "ApexClass",
                          })
                        }
                        className="text-[10px] text-primary hover:underline"
                      >
                        Open {snap.target} in editor →
                      </button>
                    )}

                    {snap.callers.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">
                          Direct Callers
                        </div>
                        <div className="space-y-0.5">
                          {snap.callers.map((c, ci) => (
                            <button
                              type="button"
                              key={`${c.id || c.name || "caller"}-${ci}`}
                              onClick={() => openByItem(c)}
                              className="w-full text-left px-1.5 py-0.5 text-[11px] text-blue-600 dark:text-blue-400 hover:underline hover:bg-blue-500/10 rounded transition-colors truncate"
                              title={`Open ${c.name} in editor`}
                            >
                              {c.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {snap.tests.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-0.5">
                          Test Classes
                        </div>
                        <div className="space-y-0.5">
                          {snap.tests.map((t, ti) => (
                            <button
                              type="button"
                              key={`${t.id || t.name || "test"}-${ti}`}
                              onClick={() => openByItem(t)}
                              className="w-full text-left px-1.5 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline hover:bg-emerald-500/10 rounded transition-colors truncate"
                              title={`Open ${t.name} in editor`}
                            >
                              {t.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {snap.status === "awaiting_user_scope" && (
                      <div className="pt-1.5 border-t border-border">
                        <div className="text-[10px] text-muted-foreground font-medium mb-1">
                          Choose scope:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            onClick={() => sendApproval(snap, "all")}
                            className="px-2 py-0.5 text-[10px] bg-primary/10 text-primary hover:bg-primary/20 rounded"
                          >
                            Update all
                          </button>
                          <button
                            type="button"
                            onClick={() => sendApproval(snap, "callers")}
                            className="px-2 py-0.5 text-[10px] bg-blue-500/10 text-blue-700 dark:text-blue-400 hover:bg-blue-500/20 rounded font-medium"
                          >
                            Callers
                          </button>
                          <button
                            type="button"
                            onClick={() => sendApproval(snap, "tests")}
                            className="px-2 py-0.5 text-[10px] bg-emerald-500/10 text-emerald-800 dark:text-emerald-400 hover:bg-emerald-500/20 rounded font-medium"
                          >
                            Tests
                          </button>
                          <button
                            type="button"
                            onClick={() => sendApproval(snap, "skip")}
                            className="px-2 py-0.5 text-[10px] bg-muted/50 text-muted-foreground hover:bg-muted/70 rounded"
                          >
                            Skip
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="text-[9px] text-muted-foreground">
                      captured{" "}
                      {snap.captured_at?.slice(0, 16).replace("T", " ")}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-muted-foreground text-center py-2 text-xs">
          No dependency data yet — ask me to refactor a class
        </div>
      )}
    </PanelSection>
  );
}
